import { useState, useRef, useEffect, FormEvent } from "react";

const steps = [
  { icon: "ri-calendar-check-line", title: "日時を選ぶ", desc: "ご希望の日程・時間帯をお選びください" },
  { icon: "ri-sparkling-line", title: "メニューを選ぶ", desc: "ご希望のネイルメニューをお知らせください" },
  { icon: "ri-check-double-line", title: "予約完了", desc: "確認メールをお送りします" },
];

export default function BookingSection() {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      await fetch("https://readdy.ai/api/form/d76hfqtr5g02gnd6a0t0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-20 lg:py-28 bg-[#FAFAF8]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-['DM_Sans'] text-xs font-medium tracking-[0.3em] uppercase text-[#C9A87C] mb-3">Reservation</p>
          <h2 className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#1A1A1A]">ご予約</h2>
          <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] mt-3">オンラインでかんたんにご予約いただけます</p>
        </div>

        {/* Steps */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-14 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {steps.map((step, i) => (
            <div key={i} className="relative flex items-start gap-4 bg-[#F5F0EB] rounded-[16px] p-6">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#3D2E2A] rounded-full">
                <i className={`${step.icon} text-white text-base`}></i>
              </div>
              <div>
                <p className="font-['DM_Sans'] text-[10px] font-semibold tracking-[0.15em] uppercase text-[#C9A87C] mb-1">Step {i + 1}</p>
                <p className="font-['Noto_Sans_JP'] text-sm font-medium text-[#1A1A1A] mb-1">{step.title}</p>
                <p className="font-['Noto_Sans_JP'] text-xs text-[#9E9189]">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className={`max-w-2xl mx-auto transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="bg-white rounded-[24px] p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-[#F5F0EB] rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-[#C9A87C]"></i>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1A1A1A] mb-3">ご予約を受け付けました</h3>
                <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B]">確認メールをお送りします。<br />担当スタッフよりご連絡いたします。</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 text-sm text-[#C9A87C] underline cursor-pointer font-['Noto_Sans_JP']">
                  別の予約をする
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1A1A1A] mb-6">予約フォーム</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">お名前 <span className="text-[#C9A87C]">*</span></label>
                    <input name="name" required type="text" placeholder="山田 花子" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] placeholder-[#C5C0BB] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                  </div>
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">電話番号 <span className="text-[#C9A87C]">*</span></label>
                    <input name="phone" required type="tel" placeholder="090-0000-0000" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] placeholder-[#C5C0BB] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                  </div>
                </div>
                <div>
                  <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">メールアドレス <span className="text-[#C9A87C]">*</span></label>
                  <input name="email" required type="email" placeholder="example@email.com" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] placeholder-[#C5C0BB] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">ご希望日 <span className="text-[#C9A87C]">*</span></label>
                    <input name="date" required type="date" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                  </div>
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">ご希望時間 <span className="text-[#C9A87C]">*</span></label>
                    <select name="time" required className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8] cursor-pointer">
                      <option value="">時間を選ぶ</option>
                      <option>10:00</option><option>11:00</option><option>12:00</option>
                      <option>13:00</option><option>14:00</option><option>15:00</option>
                      <option>16:00</option><option>17:00</option><option>18:00</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">ご希望メニュー <span className="text-[#C9A87C]">*</span></label>
                  <select name="menu" required className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8] cursor-pointer">
                    <option value="">メニューを選ぶ</option>
                    <option>シンプルワンカラー (¥6,600)</option>
                    <option>フレンチ / グラデーション (¥8,800)</option>
                    <option>デザインコース (¥9,900〜)</option>
                    <option>ハンドペイントアート (¥13,200)</option>
                    <option>フラワー3Dアート (¥16,500)</option>
                    <option>オーロラ/グラデーション (¥11,000)</option>
                    <option>ネイルスパ (¥6,600)</option>
                    <option>フットジェル (¥8,800)</option>
                  </select>
                </div>
                <div>
                  <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">ご要望・参考デザインなど</label>
                  <textarea name="message" rows={3} maxLength={500} placeholder="ご希望のデザインや参考にしたい画像のURLなどをご記入ください（500字以内）" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] text-[#1A1A1A] placeholder-[#C5C0BB] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8] resize-none" />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-[#3D2E2A] text-white text-sm font-medium py-4 rounded-full hover:bg-[#C9A87C] transition-colors duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans'] tracking-wider disabled:opacity-60">
                  {loading ? "送信中..." : "予約を申し込む"}
                </button>
                <p className="font-['Noto_Sans_JP'] text-[11px] text-[#9E9189] text-center">
                  予約完了後、担当スタッフよりご連絡いたします。水曜日は定休日です。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
